// @safetyhub/report-composer — 카탈로그 파라미터 재resolve 트리거 축(rc·web·admin 공용)
//
// "방식 값이 바뀌면 서버 prefill을 재조회한다"의 **판정 기준**을 한 곳에 모은 순수 함수 층이다.
// 기준은 카탈로그 파라미터 지정(`template.catalogParams`)이 소유한다 — **"참조된 변수의 값이
// 바뀌었나"**. `{from:'variable', key:'X'}`로 지정해 두고 X를 바꿨는데 재조회가 안 걸리면, 서버는
// 새 방식으로 조립할 준비가 됐는데 클라가 요청을 안 하는 어긋남이 된다.
// (종전의 `drivesFindingRiskMethod` 마커 축은 2026-08-08 제거 — dev·prod 전 템플릿이 파라미터
//  지정으로 이전 완료, 결정 2026-08-06-catalog-param-binding-axis §결론 6.)
//
// ⚠️ **서버 해소 규칙의 거울이다.** 서버 정본은 `ReportComposerService.resolveFindingRiskMethod`
// (`apps/server/src/report-composer/report-composer.service.ts`)이고 규칙은 2단이다:
//   ① `catalogParams['risk-assessment-config'].method` 지정 → literal 값 / variable이면 content[key]
//   ② 지정이 없으면 값 없음 → 기존 계층(점검→현장→시스템 기본) 폴백
// 지정이 있는데 값이 비어도(변수 미입력 등) 값 없음으로 끝난다(→ 계층 폴백) — 지정이 축을 소유한다.

import { RISK_METHOD_CATALOG_KEY, type ReportTemplate } from '@safetyhub/shared';

/**
 * 위험도 방식 축의 카탈로그 키 — 정본은 shared `RISK_METHOD_CATALOG_KEY`(서버·web도 같은 것을
 * import). rc 공개 API(`index.ts`) 호환을 위해 여기서 re-export한다.
 * 파라미터 이름은 서버 `report-composer.service.ts`의 동명 상수와 **쌍**이다(서버는 해소 정본이라
 * 자체 상수를 갖고, 여기는 그 규칙을 거울로 재현하는 클라이언트 층이다).
 */
export { RISK_METHOD_CATALOG_KEY };
export const RISK_METHOD_PARAM_NAME = 'method';

/**
 * `template.catalogParams`의 모든 `{from:'variable'}` 지정이 참조하는 변수 키.
 *
 * 카탈로그·파라미터를 가리지 않고 전부 모은다 — 파라미터 지정은 "이 카탈로그의 해소가 이 변수에
 * 의존한다"는 선언이므로, 어느 카탈로그의 것이든 값이 바뀌면 해소 결과가 달라질 수 있다.
 *
 * 이 집합이 곧 **재resolve 트리거 키**이자 재resolve 병합에서 **fresh로 덮지 않을**(= 사용자가 고른
 * 값인) 변수 키다. 지정이 `{from:'literal'}` 하나뿐이면 집합이 빈다 — 작성 중 바뀔 수 있는 값이
 * 없으니 옳다(고정 값 지정은 서버가 prefill 진입점에서 이미 정본으로 덮는다).
 */
export function collectCatalogParamVariableKeys(
  template: ReportTemplate | null | undefined,
): Set<string> {
  const keys = new Set<string>();
  for (const params of Object.values(template?.catalogParams ?? {})) {
    for (const binding of Object.values(params ?? {})) {
      if (binding?.from === 'variable' && typeof binding.key === 'string' && binding.key !== '') {
        keys.add(binding.key);
      }
    }
  }
  return keys;
}

/**
 * 지금 작성값에서 해소되는 **위험도 방식 값** — 서버 `resolveFindingRiskMethod`의 값 부분 거울.
 *
 * 클라가 `getComposerPrefill(..., { findingRiskMethod })`로 실어 보내는 값이다. prefill 계약은
 * 유지하되(결정 §2-a) 그 한 값을 **서버와 같은 규칙으로** 고르게 만드는 것이 P4의 요점이다 —
 * 서버 진입점은 작성값이 없어 `{from:'variable'}`을 해소하지 못하고 클라 전송값을 그대로 쓴다.
 *
 * 값으로 인정하는 형상도 서버와 같다: **비어 있지 않은 문자열**만(그 외는 값 없음 → 계층 폴백).
 * 지정이 없으면 값 없음이다(계층 폴백이 정한다).
 */
export function resolveFindingRiskMethodValue(
  template: ReportTemplate | null | undefined,
  values: Record<string, unknown>,
): string | undefined {
  const asValue = (raw: unknown): string | undefined =>
    typeof raw === 'string' && raw !== '' ? raw : undefined;

  const binding = template?.catalogParams?.[RISK_METHOD_CATALOG_KEY]?.[RISK_METHOD_PARAM_NAME];
  if (!binding) return undefined;
  // 지정이 축을 소유한다 — 값이 비어도 값 없음으로 끝난다(서버와 동일).
  return binding.from === 'literal' ? asValue(binding.value) : asValue(values[binding.key]);
}

/** 값 하나의 안정 표기 — `undefined`(미입력)와 문자열 `"undefined"`가 구분되도록 JSON 표기를 쓴다. */
function stableValueToken(value: unknown): string {
  try {
    return JSON.stringify(value) ?? 'undefined';
  } catch {
    // 순환 참조 등 직렬화 불가(변수 값은 스칼라라 실제로는 도달하지 않는다) — 비교 가능한 최선으로.
    return String(value);
  }
}

/**
 * 재resolve 요청을 결정하는 입력 전체의 시그니처 — React effect deps/변경 감지 기준으로 쓰는
 * **스칼라 한 개**.
 *
 * 두 조각을 합친다:
 *  1. 트리거 변수들(`collectCatalogParamVariableKeys`)의 현재 값(정렬된 키-값 표기) — 트리거가
 *     여럿으로 늘어난 뒤로는 "값 하나를 ref와 비교"가 성립하지 않는다. 키는 점 없는 flat 식별자
 *     (저작 UI가 강제)라 구분자와 충돌하지 않는다.
 *  2. 해소된 방식 값 — **저작 중(admin) 지정 자체가 바뀌는 경우**를 잡는다. `{from:'literal'}` 지정의
 *     값을 고치거나 지정을 붙였다 떼면 변수 값은 그대로인데 서버에 보낼 값이 달라진다.
 *
 * 트리거·지정이 아예 없는 양식에서는 값이 상수라 변경으로 오인되지 않는다. 호출부는 이 문자열로
 * "재resolve 축 있음/없음"을 판정하지 말고 `collectCatalogParamVariableKeys(...).size`를 볼 것.
 */
export function methodTriggerSignature(
  template: ReportTemplate | null | undefined,
  values: Record<string, unknown>,
): string {
  const vars = [...collectCatalogParamVariableKeys(template)]
    .sort()
    .map((key) => `${key}=${stableValueToken(values[key])}`)
    .join('|');
  return `${vars}#method=${stableValueToken(resolveFindingRiskMethodValue(template, values))}`;
}
